<script>
	import Scroller from "@sveltejs/svelte-scroller";
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";

	import MediaQuery, { breakpoint, platform, orientation } from "../../components/MediaQuerySsr.svelte";
	import Slide from "./components/Slide.svelte";
	import LineChart from "./components/LineChart.svelte";
	import throttle from "lodash.throttle";

	import { scrollTo } from "../../utils";

	let count;
	let index;
	let sectionProgress;
	let progress;
	let top = 0;
	let threshold = 0;
	let bottom = 0;

	let mounted = false;

	onMount(() => (mounted = true));

	const sections = [
		{
			id: "1",
			text: "<strong>33.083 Demos</strong> gab es seit 1. Januar 2018 in Berlin. In den letzten zwölf Monaten waren es 6975 – durchschnittlich 19 pro Tag.",
			color: "#FFFDD7",
			background: {
				// noAnimation: true,
				yTicks: [0, 200, 400, 600, 800],
				dataValue: "count",
				series: [
					{
						topic: "all",
						color: "#FFFDD7",
						label: "Demo-Anzahl",
						labelPosition: { left: 12, top: 10 },
					},
				],
			},
		},
		{
			id: "2",
			text: "Die <strong>Zahl der monatlichen Demos</strong> hat seit 2018 leicht zugenommen. Bloß während der Corona-Lockdowns brach sie ein. Das zeigt eine exklusive Auswertung von Polizeidaten durch <a target='_blank' href='//fragdenstaat.de/'>FragDenStaat</a> und Tagesspiegel.",
			color: "#FFFDD7",
			background: {
				yTicks: [0, 200, 400, 600, 800],
				dataValue: "count",
				series: [
					{
						topic: "all",
						color: "#FFFDD7",
						label: "Demo-Anzahl",
						labelPosition: { left: 12, top: 10 },
						rangeAnnotations: [
							{
								label: "1. Lockdown",
								dateLabel: "März 2020",
								startValue: new Date("2020-03-01"),
								endValue: new Date("2020-04-01"),
								labelPosition: { bottom: 12 },
							},
							{
								label: "2. Lockdown",
								dateLabel: "Okt. 2020",
								startValue: new Date("2020-10-01"),
								endValue: new Date("2021-04-01"),
								labelPosition: { bottom: 2 },
							},
						],
					},
				],
			},
		},
		{
			id: "3",
			text: "Während die Zahl der Demos leicht zugenommen hat, bleibt die der <strong>Teilnehmenden</strong> relativ gleich. Das wird deutlich bei einem Blick auf den längerfristigen <strong>Trend</strong>. ",

			color: "#FFFDD7",
			background: {
				yTicks: [0, 100, 200, 300, 400],
				// yTicks: [0, 25, 50, 75, 100],
				dataValue: "sum_participants_actually",
				series: [
					// {
					// 	// add an id to avoid duplicate keys error
					// 	id: "all-values",
					// 	topic: "all",
					// 	color: "#FFFDD7",
					// 	noAnimation: true,
					// 	faded: true,
					// 	hideFinalValueLabel: true,
					// },
					{
						topic: "all",
						color: "#FFFDD7",
						label: "Teilnehmende (Trend)",
						labelPosition: { left: 45, top: 60 },
						trendline: true,
						hideFinalValueLabel: true,
					},
				],
			},
		},
		{
			id: "4",
			text: "Die <strong>monatliche Teilnehmenden-Zahl</strong> an allen Demos hingegen zeigt: Es gibt Ausreißer. Große Veranstaltungen bringen bis zu 350.000 Menschen auf die Straße. Bei Teilnehmenden-Auswertungen sind nur Veranstaltungen berücksichtigt, bei denen die Polizei gezählt hat (73 Prozent). ",
			color: "#FFFDD7",
			background: {
				yTicks: [0, 100, 200, 300, 400],
				dataValue: "sum_participants_actually",
				series: [
					{
						topic: "all",
						color: "#FFFDD7",
						label: "Demo-Teilnehmende",
						labelPosition: { left: 45, top: 60 },
						hideFinalValueLabel: true,
					},
				],
			},
		},
		{
			id: "5",
			text: "Welche Themen mobilisieren? Anhand der Kategorien von Bewegungsforschern haben wir allen 33.083 Demos seit 2018 Themen zugeordnet. <strong>Die wichtigsten Großthemen</strong>: <strong style='color: #4692CA'>1.&nbsp;Bürgerrechte und Gleichstellung</strong>, <strong style='color: #CB4F83'>2.&nbsp;internationale Politik</strong>, <strong style='color: #82999A'>3.&nbsp;Klima, Umwelt, Energie</strong>, <strong style='color: #A35BFE'>4.&nbsp;Arbeits- und Sozialpolitik</strong>. ",
			color: "#FFFDD7",
			background: {
				yTicks: [0, 20, 40, 60],
				dataValue: "sum_participants_actually",
				series: [
					{
						topic: "buergerrechte, gleichstellung, diskriminierung",
						color: "#4692CA",
						label: "1. Bürgerrechte, Gleichstellung",
						labelPosition: { left: 25, top: 5 },
						hideFinalValueLabel: true,
						trendline: true,
					},
					{
						topic: "internationale politik",
						color: "#CB4F83",
						label: "2. Internationale Politik",
						labelPosition: { left: 25, top: 10 },
						hideFinalValueLabel: true,
						trendline: true,
					},
					{
						topic: "klima, umwelt und energie",
						color: "#82999A",
						label: "3. Klima, Umwelt, Energie ",
						labelPosition: { left: 25, top: 15 },
						hideFinalValueLabel: true,
						trendline: true,
					},
					{
						topic: "arbeits- und sozialpolitik",
						color: "#A35BFE",
						label: "4. Arbeits- und Sozialpolitik",
						labelPosition: { left: 25, top: 20 },
						hideFinalValueLabel: true,
						trendline: true,
					},
				],
			},
		},
		{
			id: "6",
			text: "<strong>Die Klimabewegung</strong> mobilisiert offenbar nicht mehr so viele wie vor der Pandemie. ",
			color: "#82999A",
			background: {
				yTicks: [0, 10, 20, 30],
				dataValue: "sum_participants_actually",
				series: [
					{
						topic: "klima, umwelt und energie",
						color: "#82999A",
						label: "Klima",
						labelPosition: { left: 40, top: 45 },
						trendline: true,
						hideFinalValueLabel: true,
					},
				],
			},
		},
		{
			id: "7",
			text: "Und das, obwohl es weiterhin viele <strong>Veranstaltungen zum Thema Klima</strong> gibt. Seit Anfang 2022 organisiert die „Letzte Generation“ Straßenblockaden. ",
			color: "#82999A",
			background: {
				yTicks: [0, 50, 100, 150, 200],
				dataValue: "count",
				series: [
					{
						topic: "klima, umwelt und energie",
						color: "#82999A",
						label: "Klima",
						labelPosition: { left: 40, top: 45 },
						pointAnnotations: [
							{
								label: "112 Aktionen der<br>„Letzten Generation“",
								dateLabel: "Mai 2023",
								date: new Date("2023-05"),
								value: 160,
								valueLabel: "",
							},
							{
								label: "Erste<br>F4F-Demo<br>in Berlin",
								dateLabel: "18.01.2019",
								date: new Date("2019-01"),
								value: 25,
								valueLabel: "550",
								labelOffset: 25,
							},
							{
								label: "Größte F4F-Demo<br>in Berlin",
								dateLabel: "20.09.2019",
								date: new Date("2019-09"),
								value: 125,
								valueLabel: "100.000",
							},
						],
					},
				],
			},
		},
		{
			id: "8",
			text: "Als die <strong>Klimademo-Teilnehmenden</strong> weniger wurden, folgte Corona. Pandemie und Lockdowns veränderten schlagartig das Leben aller und das Demo-Geschehen – bis der Ukraine-Krieg dasselbe tat.",
			color: "#82999A",
			background: {
				yTicks: [0, 10, 20, 30],
				dataValue: "sum_participants_actually",
				series: [
					{
						topic: "klima, umwelt und energie",
						color: "#82999A",
						label: "Klima",
						labelPosition: { left: 40, top: 45 },
						faded: true,
						trendline: true,
						hideFinalValueLabel: true,
					},
					{
						topic: "internationale politik --- krieg und frieden",
						color: "#A35BFE",
						label: "Krieg & Frieden",
						labelPosition: { top: 60, left: 70 },
						trendline: true,
						hideFinalValueLabel: true,
					},
					{
						topic: "gesundheit --- corona",
						color: "#61B6C9",
						label: "CORONA",
						labelPosition: { top: 70, left: 50 },
						trendline: true,
						hideFinalValueLabel: true,
					},
				],
			},
		},
		{
			id: "9",
			text: "Seit Ende 2022 nimmt die Zahl der <strong>Demos zu Corona</strong> langsam ab. Aber auffällig ist: Das Thema ist weiterhin präsent. ",
			color: "#61B6C9",
			background: {
				dataValue: "count",
				yTicks: $platform === "mobile" ? [0, 50, 100, 150, 200] : [0, 50, 100, 150],
				series: [
					{
						topic: "gesundheit --- corona",
						color: "#61B6C9",
						label: "Corona",
						labelPosition: { top: 50 },
						rangeAnnotations: [
							{
								label: "1. Lockdown",
								dateLabel: "März 2020",
								startValue: new Date("2020-03-01"),
								endValue: new Date("2020-04-01"),
								labelPosition: { top: 5 },
							},
							{
								label: "2. Lockdown",
								dateLabel: "Okt. 2020",
								startValue: new Date("2020-10-01"),
								endValue: new Date("2021-04-01"),
								labelPosition: { top: 15 },
							},
						],
					},
				],
			},
		},
		{
			id: "10",
			text: "Mit 22.500 Teilnehmern war der „Tag der Freiheit“ gegen die Maßnahmen im August 2020 die größte <strong>Demo zum Thema Corona</strong>. Im selben Monat folgte der „Sturm auf den Reichstag“ mit hunderten Teilnehmern. ",
			color: "#61B6C9",
			background: {
				dataValue: "count",
				yTicks: $platform === "mobile" ? [0, 50, 100, 150, 200] : [0, 50, 100, 150],
				series: [
					{
						topic: "gesundheit --- corona",
						color: "#61B6C9",
						label: "Corona",
						labelPosition: { top: 50 },
						pointAnnotations: [
							{
								label: "„Tag der Freiheit“-<br>Demo",
								date: new Date("2020-08"),
								dateLabel: "01.08.2020",
								value: 60,
								valueLabel: "22.500",
								// labelPosition: { top: -10 }
							},
						],
					},
				],
			},
		},
		{
			id: "11",
			text: "Die meisten Demos gibt es in Berlin für <strong>Anliegen mit internationalem Bezug</strong>. Seit Beginn des Ukraine-Kriegs hat das Thema weiter an Bedeutung gewonnen. ",
			color: "#CB4F83",
			background: {
				dataValue: "count",
				yTicks: [0, 100, 200, 300, 400, 500],
				series: [
					{
						topic: "internationale politik",
						color: "#CB4F83",
						label: "internationale politik",
						labelPosition: { left: 35, top: 45 },
						pointAnnotations: [
							{
								label: "Vermehrt Demos<br>zum Geflüchteten-Lager<br>in Moria",
								dateLabel: "",
								date: new Date("2020-07"),
								value: 400,
								// valueLabel: '',
								// labelPosition: { top: -10 }
							},
							{
								label: "Iran-<br>Solidaritätsdemo",
								dateLabel: "22.10.2022",
								date: new Date("2022-10"),
								value: 340,
								valueLabel: "80.000",
								labelPosition: { top: -10 },
							},
						],
					},
				],
			},
		},
		{
			id: "12",
			text: "Der Anstieg der <strong>Teilnehmenden bei internationalen Themen</strong> ist nicht allein Folge des Krieges. Im August 2023 kamen 50.000 Menschen aus ganz Europa zum Großen Stern, um sich mit Frauen im Iran zu solidarisieren: Berlin wurde zur weltpolitischen Bühne. ",
			color: "#CB4F83",
			background: {
				dataValue: "sum_participants_actually",
				yTicks: [0, 5, 15, 25],
				series: [
					{
						topic: "internationale politik",
						color: "#CB4F83",
						label: "internationale politik",
						labelPosition: { left: 35, top: 45 },
						hideFinalValueLabel: true,
						trendline: true,
					},
				],
			},
		},
		{
			id: "13",
			text: "Auch Lokales ist präsent. „Deutsche Wohnen und Co. enteignen“ mobilisierte vor dem ersten Volksentscheid 2021. Danach zog das <strong>Thema Wohnen und Mieten</strong> seitdem weniger Menschen an als davor. Vielleicht ändert sich das bald wieder: DW enteignen bringt ein neues Referendum auf den Weg. ",
			color: "#DF9A69",
			background: {
				yTicks: [0, 5, 15, 25],
				dataValue: "sum_participants_actually",
				series: [
					{
						topic: "internationale politik",
						color: "#CB4F83",
						label: "internationale politik",
						labelPosition: { left: 35, top: 45 },
						faded: true,
						hideFinalValueLabel: true,
						trendline: true,
					},
					{
						topic: "stadtentwicklung und wohnen",
						color: "#DF9A69",
						label: "Wohnen & Mieten",
						labelPosition: { top: 70, left: 67 },
						hideFinalValueLabel: true,
						trendline: true,
						pointAnnotations: [
							{
								label: "„DW enteignen”-<br>Referendum",
								dateLabel: "26.09.2021",
								date: new Date("2021-08"),
								value: 5,
								// valueLabel: '',
								labelPosition: { top: -40 },
							},
						],
					},
				],
			},
		},
		{
			id: "14",
			text: "Über die Jahre wichtiger wird außerdem das <strong>Thema Gleichstellung</strong>: Frauenrechte, sexuelle Selbstbestimmung, Inklusion, die Rechte Queerer und der Opfer von Rassismus. Zählt man die Christopher Street Days mit, ist es das teilnehmerstärkste Thema. ",
			color: "#CE7A93",
			background: {
				yTicks: [0, 20, 40, 60],
				dataValue: "sum_participants_actually",
				series: [
					{
						topic: "buergerrechte, gleichstellung, diskriminierung",
						color: "#CE7A93",
						label: "gleichstellung",
						labelPosition: { top: 60 },
						trendline: true,
						hideFinalValueLabel: true,
					},
				],
			},
		},
	];

	sections.map((s, i) => ({ ...s, index: i }));
	const sectionsById = sections.reduce((obj, item) => ({ ...obj, [item.id]: item }), {});
	const allYTicks = [...new Set(sections.map(({ background }) => background.yTicks).flat())]
		.filter((d) => !isNaN(d))
		.sort((a, b) => (a > b ? 1 : -1));

	let currentSection = sections[0].id;

	const changeSection = throttle(() => {
		currentSection = index ? sections[index].id : sections[0].id;
	}, 700);

	$: if (mounted) index, changeSection();
	$: currentSectionObj = sectionsById[currentSection];

	// a value between 1 and 0.2 that can be used as a factor for the offset of the background chart
	// 1: the background chart is moved to the top of the container, i.e., the position before the scrolly part begins
	// 0.2: the background chart is in the center, i.e., the position for during the scrolly
	const startTransition = -0.02; // start the transition to the center when we reach this progress value
	$: normalizedProgressBeforeBeginning = Math.max(0.2, Math.min(1, progress / startTransition));
</script>

<MediaQuery />

<div class="line-chart-scrolly">
	<Scroller {top} {threshold} {bottom} bind:count bind:index bind:offset={sectionProgress} bind:progress>
		<div slot="background" class="background">
			<!-- <div class="content-container" style:top={progress >= -0.01 ? "-10%" : null}> -->
			<div class="content-container" style="--factor: {normalizedProgressBeforeBeginning}">
				<LineChart {allYTicks} info={{ ...currentSectionObj.background }} />
			</div>
		</div>

		<div class="foreground" slot="foreground">
			{#each sections as section, i}
				<section class="section-{i}" class:lead={i === 0} bind:this={section.elem} bind:clientHeight={section.height}>
					<Slide headline={section.headline} text={section.text} color={section.color} />
				</section>
			{/each}
		</div>
	</Scroller>
</div>

<style lang="scss">
	:root {
		--background-height: 100vh;

		// calc the distance to the top of the chart which is the distance between the top of the chart and the top of the container that contains the chart
		// basically: the background container height minus the chart height divided by 2 --> and negative to move the chart up
		--distance-to-top-of-chart: calc((var(--background-height) - var(--chart-height-with-margin)) / 2);
	}

	.line-chart-scrolly {
		font-family: $ff-sans;
		user-select: none;

		// calculate the remaining empty room at the bottom before the scrolly ends. Basically the height of the whole background minus the distance from top of the background to the chart
		// + additional 30px for a bit more distance
		margin-bottom: calc(var(--background-height) - var(--distance-to-top-of-chart) + 30px);
	}

	[slot="background"] {
		height: 100vh;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	.content-container {
		position: relative;

		// offset the chart to be at the top of the container when the scrolly part begins and then slowly move further down based on the factor
		transform: translateY(calc(-1 * var(--factor) * var(--distance-to-top-of-chart)));
	}

	.foreground {
	}

	section {
		height: 110vh;
		position: relative;
		display: flex;
		justify-content: center;

		@media (max-width: $breakpoint-mobile-l) {
			height: 130vh;
			margin: 0 10%;
		}

		// &:first-of-type {
		// 	// padding-top: 77vh;
		// }
	}
</style>
